import { Space, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { getMessageTypeByDeclaration } from 'views/declarations/utils/declaration-utils';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { Declaration } from 'store/declarations/declaration';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import ExistingDeclarationInfo from './ExistingDeclarationInfo';
import {
    canReplaceAllMirroredDeclarations,
    canReplaceMirroredDeclaration,
    getMirroredDeclarationExistsText,
    getMultipleMirroredDeclarationsExistText,
} from './getMirroredDeclarationExistsText';

type Props = {
    open: boolean;
    declarations: Declaration[];
    manifestId?: string;
    jobId?: string;
    onGenerateNew: () => void;
    onViewExisting: () => void;
    onClose: () => void;
};

const MirroredDeclarationAlreadyExistsModal = ({
    open,
    declarations,
    manifestId,
    jobId,
    onGenerateNew,
    onViewExisting,
    onClose,
}: Props) => {
    const [firstDeclaration] = declarations;
    const messageType = getMessageTypeByDeclaration(firstDeclaration) ?? 'UNKNOWN MESSAGE TYPE';
    const hasMultipleDeclarations = declarations.length > 1;

    const modalContentStyle = { paddingInline: 16, paddingBottom: 16, width: '100%' };

    // Several mirrored declarations: there is no single one to "view", so each is a link instead and
    // the user can only generate new ones if every existing one can be replaced.
    if (hasMultipleDeclarations) {
        const canGenerateNewMirroredDeclarations = canReplaceAllMirroredDeclarations(declarations);
        const text = getMultipleMirroredDeclarationsExistText(declarations, messageType);

        return (
            <ConfirmationModal
                title={`${messageType}s Already Exist`}
                width={640}
                type="warning"
                open={open}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={onGenerateNew}
                confirmButtonText={`Generate New ${messageType}s`}
                confirmationButtonDisabled={!canGenerateNewMirroredDeclarations}
                cancelButtonText="Cancel"
                icon={<WarningOutlined />}
                content={
                    <Space size={12} direction="vertical" style={modalContentStyle}>
                        <Typography.Text>{text}</Typography.Text>
                        {declarations.map((declaration) => (
                            <ExistingDeclarationInfo
                                key={declaration.id}
                                declaration={declaration}
                                to={getDeclarationUrl({ manifestId, jobId, declarationId: declaration.id })}
                            />
                        ))}
                    </Space>
                }
            />
        );
    }

    const canGenerateNewMirroredDeclaration = canReplaceMirroredDeclaration(firstDeclaration.status);

    const title = `${messageType} Already Exists`;
    const confirmationModalText = canGenerateNewMirroredDeclaration
        ? `Generate New ${messageType}`
        : `View Existing ${messageType}`;
    const cancelButtonText = canGenerateNewMirroredDeclaration ? `View Existing ${messageType}` : 'Cancel';

    const text = getMirroredDeclarationExistsText(firstDeclaration.status, messageType);

    return (
        <ConfirmationModal
            title={title}
            width={640}
            type="warning"
            open={open}
            onClose={onClose}
            onCancel={canGenerateNewMirroredDeclaration ? onViewExisting : onClose}
            onConfirm={canGenerateNewMirroredDeclaration ? onGenerateNew : onViewExisting}
            confirmButtonText={confirmationModalText}
            cancelButtonText={cancelButtonText}
            icon={<WarningOutlined />}
            content={
                <Space size={12} direction="vertical" style={modalContentStyle}>
                    <Typography.Text>{text}</Typography.Text>
                    <ExistingDeclarationInfo
                        declaration={firstDeclaration}
                        to={getDeclarationUrl({ manifestId, jobId, declarationId: firstDeclaration.id })}
                    />
                </Space>
            }
        />
    );
};

export default MirroredDeclarationAlreadyExistsModal;
